import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DatasetFileTypeOption, FileAction, FileDatasetValidationStatus, FileModel, Folder, FolderViewModel, ItemsToBeRemovedOptions } from '@ca/core/models';
import { ExperimentalFeaturesService, FilePreviewService, WindowRef } from '@ca/core/services';
import { hasFailedConversion, hasUnknownValidationStatus, isConvertedAndHasInvalidValidationStatus } from '@ca/utils';
import { ArivisService } from 'src/app/core/services/arivis.service';
import { FileExplorerMode } from '../../../core/types/file-explorer-mode.type';
import { FILE_EXPLORER_MODE } from '../../constants/constants';

@Component({
    selector: 'ca-file-actions',
    templateUrl: './file-actions.component.html',
    styleUrls: ['./file-actions.component.scss'],
})
export class FileActionsComponent {
    /**
     * List of available file actions. Updates the state of action buttons based on the provided actions.
     */
    @Input() set actions(actions: FileAction[]) {
        this._actions = actions;
        this.isDownloadBtnEnabled = this.actions.includes('download');
        this.isRenameBtnEnabled = this.actions.includes('rename');
        this.isDeleteBtnEnabled = this.mode === FILE_EXPLORER_MODE.MODIFICATION && this.actions.includes('delete');
        this.isRemoveBtnEnabled = this.mode === FILE_EXPLORER_MODE.MODIFICATION && this.actions.includes('remove');
    }

    /**
     * Retrieves the list of available file actions for the component.
     * This determines which action buttons (e.g., 'download', 'rename', etc.) are enabled.
     * @returns {FileAction[]} An array of available file actions.
     */
    get actions(): FileAction[] {
        return this._actions;
    }

    /**
     * Retrieves the current file or folder item associated with the component.
     * This object contains the file or folder's details and is used to display actions or status.
     * @returns {FileModel & Folder} The file or folder object associated with the component.
     */
    get item(): FileModel & Folder {
        return this._item;
    }

    @Input() index: number = 0;
    @Input() isFolder: boolean;
    @Input() isSelected: boolean;
    @Input() mode: FileExplorerMode = FILE_EXPLORER_MODE.MODIFICATION;
    @Input() isRemoveInvalidFilesDialog?: boolean = false;
    @Input() textClasses: string[] = [];
    @Input() isSegmentation?: boolean;
    @Input() datasetFolderType?: DatasetFileTypeOption;
    @Input() datasetId?: string;
    @Input() isFilesStatusShown?: boolean = true;
    @Input() showFileStatus = false;
    @Input() truncateFileNameAtWidthOf?: number;
    /**
     * The file or folder item associated with this component. Updates status flags when set.
     */
    @Input() set item(item: FileModel & Folder) {
        this._item = item;
        if (item instanceof FileModel) {
            this.isItemConvertedAndHasInvalidValidationStatus = isConvertedAndHasInvalidValidationStatus(
                item.convertStatus || 'Unknown',
                item.validationStatus || 'Unknown',
            );

            this.isItemConversionFailed = hasFailedConversion(item.convertStatus);
            this.isValidationUnknown = hasUnknownValidationStatus(item.validationStatus as FileDatasetValidationStatus);
        }
    }

    @Output() onOpenFolder = new EventEmitter<string>();
    @Output() deleteItems = new EventEmitter<ItemsToBeRemovedOptions<FileModel & Folder>>();
    @Output() removeItems = new EventEmitter<ItemsToBeRemovedOptions<FileModel & Folder>>();

    FILE_EXPLORER_MODE = FILE_EXPLORER_MODE;
    isDownloadBtnEnabled = false;
    isRenameBtnEnabled = false;
    isDeleteBtnEnabled = false;
    isRemoveBtnEnabled = false;
    isCziConversionEnabled = this.experimentalFeaturesService.isCziConversionFeatureEnabled;
    isItemConvertedAndHasInvalidValidationStatus = false;
    isValidationUnknown = false;
    isItemConversionFailed = false;
    isMEDProjectEnabled = this.experimentalFeaturesService.isMEDProjectActive;

    private _actions: FileAction[] = [];
    private _item: FileModel & Folder;

    constructor(
        private experimentalFeaturesService: ExperimentalFeaturesService,
        private filePreviewService: FilePreviewService,
        private arivisService: ArivisService,
        private windowRef: WindowRef,
    ) {}

    /**
     * Determines if a file is supported by the Arivis viewer.
     * @param item - File to check.
     * @returns `true` if the file is supported, `false` otherwise.
     */
    isSupportedByArivis(item: FileModel): boolean {
        return this.arivisService.isSupportedByArivis(item.type);
    }

    /**
     * Determines if a file type is supported by the file previewer.
     * @param type - File type to check.
     * @returns `true` if the type is supported, `false` otherwise.
     */
    isSupportedByFilePreviewer(type: string | undefined): boolean {
        return !this.isMEDProjectEnabled && this.arivisService.isSupportedByFilePreviewer(type);
    }

    /**
     * Opens a file or folder. Emits a folder open event or invokes file opening logic.
     * @param item - File or folder to open.
     * @param event - Click event.
     */
    openItem(item: FileModel | Folder | FolderViewModel, event: Event): void {
        if (this.mode !== FILE_EXPLORER_MODE.SELECTION) {
            event.stopPropagation();

            if (this.isFolder) {
                this.onOpenFolder.emit((item as Folder).id);
            } else {
                this.openFile(item as FileModel);
            }
        }
    }

    get hasError(): boolean | undefined {
        return (
            !this.isFolder &&
            this.isFilesStatusShown &&
            this.item.convertStatus &&
            this.item.convertStatus !== 'Active' &&
            this.item.convertStatus !== 'Success' &&
            this.item.validationStatus &&
            this.item.validationStatus !== 'PendingValidation' &&
            this.item.validationStatus &&
            this.item.validationStatus !== 'Valid'
        );
    }

    /**
     * Opens a file using the appropriate viewer or previewer.
     * @param file - File to open.
     */
    openFile(file: FileModel): void {
        if (this.actions.includes('view')) {
            if (this.isSupportedByArivis(file)) {
                this.openViewerUrl(file);
            }
            if (this.isSupportedByFilePreviewer(file.type!)) {
                this.filePreviewService.openWithURL(file.token, file.type!);
            }
        }
    }

    /**
     * Determines if the file name is clickable based on the file's properties.
     * @param item The file or folder item.
     * @returns {boolean} `true` if the file name should be clickable, `false` otherwise.
     */
    isFileNameClickable(item: FileModel & Folder): boolean {
        return (
            this.mode === FILE_EXPLORER_MODE.MODIFICATION &&
            this.actions.includes('view') &&
            (this.isFolder || this.isSupportedByArivis(item) || this.isSupportedByFilePreviewer(item.type))
        );
    }

    /**
     * Opens a file's viewer URL in a new browser tab. If required, appends dataset-related parameters.
     * @param file - File to open.
     */
    private openViewerUrl(file: FileModel): void {
        if (file.viewerUrl == null) {
            file.viewerUrl = this.arivisService.generateArivisMlViewerUrl(file);
        }

        let url = file.viewerUrl;
        if (this.datasetId != null && url != null) {
            if (this.datasetFolderType === DatasetFileTypeOption.FILES) {
                url += `&datasetId=${this.datasetId}`;
            }
        }
        this.windowRef.nativeWindow.open(url, '_blank');
    }
}
